import {
  BenefitsEnrolledPackage,
  BenefitsEnrollment,
  BenefitsEnrollmentCostContribution,
  BenefitsEnrollmentCostContributorEnum,
  BenefitsEnrollmentStateEnum,
  BenefitsPlan,
  BenefitsPlanPrice,
  BenefitsProvisionedEnrollment,
  BenefitsTier,
  CostSplitSetting,
  EmploymentTemplate,
  Engagement,
  EngagementType,
  Maybe,
  Money,
} from "@oysterhr/schema"
import { groupBy, sum, uniqueId } from "lodash-es"
import {
  moneyFormatSubunit,
  convertSubunitIntoUnit,
  toLocalTimezone,
  SimpleMoney,
} from "@oysterhr/common"
import { ContributorInitialValues, FieldsWithValues } from "../../types"
import { titleize } from "~/app/utils/stringHelpers"

const ACTIVE_STATES = new Set([
  BenefitsEnrollmentStateEnum.Enrolled,
  BenefitsEnrollmentStateEnum.InProgress,
  BenefitsEnrollmentStateEnum.Applied,
  BenefitsEnrollmentStateEnum.Requested,
  BenefitsEnrollmentStateEnum.AwaitingToBeFinished,
])

export const BEST_IN_CLASS_PACKAGE_NAME = "Best-in-class package"
export const COMPETITIVE_PACKAGE_NAME = "Competitive package"
export const ESSENTIAL_PACKAGE_NAME = "Essential package"
export const HEALTH_CATEGORY = "health"

export const isActiveEnrollmentState = (state: BenefitsEnrollmentStateEnum) =>
  ACTIVE_STATES.has(state)

export const countActiveBenefits = (benefitEnrollments: BenefitsEnrollment[]) => {
  return benefitEnrollments.reduce(
    (acc, enrollment) => (isActiveEnrollmentState(enrollment.state) ? acc + 1 : acc),
    0,
  )
}

export const selectActiveEnrollments = (benefitEnrollments: BenefitsEnrollment[]) => {
  return benefitEnrollments.filter(enrollment => isActiveEnrollmentState(enrollment.state))
}

export const totalPriceInvoicedCompany = (enrollments: BenefitsEnrollment[]) => {
  const enrollmentsWithPrice = enrollments.filter(
    enrollment => enrollment.totalPriceInvoicedCompany,
  )
  const enrollmentsByCurrency = groupBy(
    enrollmentsWithPrice,
    ({ totalPriceInvoicedCompany }) => totalPriceInvoicedCompany?.currencyCode,
  )
  const currencies = Object.keys(enrollmentsByCurrency)

  if (currencies.length !== 1) {
    return null
  }

  const [currency] = currencies
  const amountSubUnit = sum(
    enrollmentsWithPrice.map(enrollment => enrollment?.totalPriceInvoicedCompany?.subunit ?? 0),
  )
  return moneyFormatSubunit(amountSubUnit, currency)
}

export const extractEstimate = (priceEstimate: BenefitsPlanPrice) => {
  const mainCurrency =
    priceEstimate.currencies.find(currency => currency.currencyCode === "USD") ??
    priceEstimate.currencies[0]
  if (mainCurrency) {
    return `${mainCurrency?.currencyCode} ${convertSubunitIntoUnit(
      mainCurrency.subunit,
      mainCurrency.currencyCode,
    )}`
  }
}

export const getBenefitPlanPrice = (priceEstimate: BenefitsPlanPrice, currencyCode: string) => {
  const defaultCurrency = priceEstimate.currencies.find(c => c.currencyCode === currencyCode)
  return SimpleMoney.fromNullable(defaultCurrency)?.format()
}

function compareState(a: BenefitsEnrollmentStateEnum, b: BenefitsEnrollmentStateEnum) {
  return Number(ACTIVE_STATES.has(a)) - Number(ACTIVE_STATES.has(b))
}

function compareDates(
  a: BenefitsEnrollment["coverageStartDate"],
  b: BenefitsEnrollment["coverageStartDate"],
) {
  if (a === b) return 0

  return a > b ? -1 : 1
}

// sorts enrollments, the ones with active state first and then enrollments are sorted by the date in a descending order
export const activeAndMostRecentEnrollmentsFirst = (enrollments: BenefitsEnrollment[]) => {
  return [...enrollments].sort(
    (a, b) =>
      compareState(b.state, a.state) || compareDates(a.coverageStartDate, b.coverageStartDate),
  )
}

export const getBenefitsPlanPriceUnit = (
  currencyCode: string,
  priceEstimate?: Maybe<BenefitsPlanPrice>,
) => {
  if (!priceEstimate) return 0
  const currency = priceEstimate.currencies.find(c => c.currencyCode === currencyCode)
  if (currency) {
    return convertSubunitIntoUnit(currency.subunit, currency.currencyCode)
  }
  return 0
}

export const getBenefitsPlanFee = (currencyCode: string, fee?: Maybe<Money[]>) => {
  if (!fee) return 0
  const currency = fee.find(c => c.currencyCode === currencyCode)
  if (currency) {
    return convertSubunitIntoUnit(currency.subunit, currency.currencyCode)
  }
  return 0
}

export const sumPlanPrices = (plans: BenefitsPlan[], currencyCode: string) => {
  return plans.reduce((a, c) => a + getBenefitsPlanPriceUnit(currencyCode, c.priceEstimate), 0)
}
export const sumPlanFees = (plans: BenefitsPlan[], currencyCode: string) => {
  return Math.ceil(plans.reduce((a, c) => a + getBenefitsPlanFee(currencyCode, c.fee), 0))
}

export const getBenefitFee = (fees: Money[], currencyCode: string) => {
  return fees.find(c => c.currencyCode === currencyCode)
}

export const getBenefitFeeValue = (fees: Money[], currencyCode: string) => {
  const benefitFee = getBenefitFee(fees, currencyCode)
  const feeValue = benefitFee
    ? Math.ceil(convertSubunitIntoUnit(benefitFee.subunit, benefitFee.currencyCode))
    : 0
  return feeValue
}

export const sortTiers = (tiers: BenefitsTier[], salaryCurrencyCode: string) => {
  const sortedTiers = [...tiers]
  sortedTiers.sort((tierA: BenefitsTier, tierB: BenefitsTier) => {
    const tierAPrice = sumPlanPrices(tierA.includedPlans, salaryCurrencyCode)
    const tierBPrice = sumPlanPrices(tierB.includedPlans, salaryCurrencyCode)

    return (tierBPrice || 0) - (tierAPrice || 0)
  })
  return sortedTiers
}

export const sortAddons = (addons: BenefitsPlan[], salaryCurrencyCode: string) => {
  const sortedAddons = [...addons]
  sortedAddons.sort((addonA: BenefitsPlan, addonB: BenefitsPlan) => {
    const addonAPrice = addonA.priceEstimate
      ? (addonA.priceEstimate.currencies.find(c => c.currencyCode === salaryCurrencyCode)
          ?.subunit as number)
      : 0
    const addonBPrice = addonB.priceEstimate
      ? (addonB.priceEstimate.currencies.find(c => c.currencyCode === salaryCurrencyCode)
          ?.subunit as number)
      : 0
    return (addonBPrice || 0) - (addonAPrice || 0)
  })
  return sortedAddons
}

export const getCurrentBenefitsEnrolledPackage = (engagement?: Maybe<Engagement>) => {
  if (!engagement) return undefined
  const benefitsEnrolledPackages = engagement?.benefitsEnrolledPackages?.collection
  const currentEnrolledPackage =
    benefitsEnrolledPackages && benefitsEnrolledPackages.length
      ? benefitsEnrolledPackages[benefitsEnrolledPackages.length - 1]
      : undefined
  return currentEnrolledPackage
}

export const getAllPreviousBenefitsEnrolledPackages = (engagement?: Engagement) => {
  if (!engagement) return undefined
  const benefitsEnrolledPackages = engagement?.benefitsEnrolledPackages?.collection
  return benefitsEnrolledPackages && benefitsEnrolledPackages.length > 1
    ? benefitsEnrolledPackages.slice(0, -1)
    : undefined
}

export const getBenefitsPackagedEnrollments = (
  benefitsEnrolledPackage?: BenefitsEnrolledPackage,
) => {
  if (!benefitsEnrolledPackage) return []
  return benefitsEnrolledPackage?.enrollments.collection
}

const isSupplementaryBenefit = (enrollment: BenefitsEnrollment | BenefitsProvisionedEnrollment) =>
  !enrollment.tier

export const getSupplementaryBenefits = (engagement?: Engagement) => {
  return engagement?.benefitsEnrollments?.collection?.filter(isSupplementaryBenefit) || []
}

export const getProvisionedSupplementaryBenefits = (engagement?: Engagement) => {
  return (
    engagement?.benefitsProvisionedEnrollments?.collection?.filter(isSupplementaryBenefit) || []
  )
}

export const getActiveBenefitsPackagedEnrollments = (
  benefitsEnrolledPackage?: BenefitsEnrolledPackage,
) => {
  return selectActiveEnrollments(getBenefitsPackagedEnrollments(benefitsEnrolledPackage))
}

export const getEnrolledBenefitsPackagedEnrollments = (
  benefitsEnrolledPackage?: BenefitsEnrolledPackage,
) => {
  return selectActiveEnrollments(getBenefitsPackagedEnrollments(benefitsEnrolledPackage)).filter(
    x => x.state === BenefitsEnrollmentStateEnum.Enrolled && x.coverageEndDate === null,
  )
}

export const getBenefitPackageSupportFee = (
  paymentCurrencyCode: string,
  benefitsEnrolledPackage?: BenefitsEnrolledPackage,
) => {
  return benefitsEnrolledPackage?.tier?.fee && benefitsEnrolledPackage?.tier?.fee?.length
    ? getBenefitFee(benefitsEnrolledPackage?.tier?.fee, paymentCurrencyCode)
    : undefined
}

export const chooseDefaultPackageTier = (tiers: BenefitsTier[]) => {
  const recommended = tiers.find(tier => tier.name === COMPETITIVE_PACKAGE_NAME)
  const basic = tiers.find(tier => tier.name === ESSENTIAL_PACKAGE_NAME)
  const isOnlyBasicTier = tiers.length === 1 && tiers[0].name === ESSENTIAL_PACKAGE_NAME

  return isOnlyBasicTier ? basic : recommended
}

function findPlanSetting(
  planId: string,
  costSplitSettings: CostSplitSetting[],
): Omit<CostSplitSetting, "planId" | "__typename" | "plan"> {
  return (
    costSplitSettings.find(setting => setting.planId === planId) ?? {
      dependentsEmployerContributionMaxCount: 0,
      dependentsEmployerContribution: 0,
      employerContribution: 100,
    }
  )
}

function createPseudoProvisionedEnrollment(
  plan: BenefitsPlan,
  costSplitSettings: CostSplitSetting[],
  tier?: BenefitsTier,
): BenefitsProvisionedEnrollment {
  const costSplitSetting = findPlanSetting(plan.id, costSplitSettings)
  return {
    ...costSplitSetting,
    id: uniqueId("Benefits::Enrollment:"),
    __typename: "BenefitsProvisionedEnrollment",
    tier,
    plan,
  } as BenefitsProvisionedEnrollment
}

export const getBenefitsHiringTemplateData = ({
  engagement,
  employmentTemplate,
}: {
  engagement: Engagement
  employmentTemplate?: Maybe<EmploymentTemplate>
}) => {
  const createEnrollments = (plans: BenefitsPlan[], tier?: BenefitsTier) =>
    plans.map(plan =>
      createPseudoProvisionedEnrollment(
        plan,
        employmentTemplate?.benefitsEnrollmentTemplate?.preferredCostSplit ?? [],
        tier,
      ),
    )
  const benefitsTemplateEngagementMap = {
    ...engagement,
    benefitsEnrolledPackages: {
      collection: [{ tier: employmentTemplate?.benefitsEnrollmentTemplate?.tier }],
    },
    benefitsProvisionedAddons: {
      collection: employmentTemplate?.benefitsEnrollmentTemplate?.supplementaryBenefits,
    },
    benefitsProvisionedEnrollments: {
      collection: [
        ...(employmentTemplate?.benefitsEnrollmentTemplate?.pensions?.map(pension => {
          return {
            plan: pension.plan,
            ...(pension.costContribution && {
              enrollmentCostContributions: { collection: [pension.costContribution] },
            }),
          }
        }) ?? []),
        ...createEnrollments(
          employmentTemplate?.benefitsEnrollmentTemplate?.supplementaryBenefits ?? [],
        ),
        ...createEnrollments(
          employmentTemplate?.benefitsEnrollmentTemplate?.tier?.includedPlans ?? [],
          employmentTemplate?.benefitsEnrollmentTemplate?.tier ?? undefined,
        ),
      ],
    },
  } as Engagement

  return benefitsTemplateEngagementMap
}

export const getDefaultEngagementType = () => EngagementType.Employment

export const isFullTimeEmployee = (engagementType?: EngagementType) => {
  return engagementType?.toLowerCase() === getDefaultEngagementType().toLowerCase()
}

export const extractDependentFieldsWithValues = (values: object) => {
  const fieldsWithValues = Object.entries(values).map(([key, value]) => {
    const stringVal = typeof value === "object" ? toLocalTimezone(value) : value
    return { name: key, value: stringVal }
  }) as FieldsWithValues[]
  return fieldsWithValues
}

export const buildDependentFieldValues = (fields: FieldsWithValues[]) =>
  fields.reduce((acc: Record<string, string | undefined>, field) => {
    acc[field.name] = field.value ? field.value : ""
    return acc
  }, {})

export const extractCompanyCostContribution = (
  costContributions?: BenefitsEnrollmentCostContribution[],
) => {
  return costContributions?.find(
    contribution => contribution.contributor === BenefitsEnrollmentCostContributorEnum.Company,
  )
}

export const getBenefitsPlanName = (plan: BenefitsPlan) => {
  return `${plan.provider.name} ${plan.name}`
}

export const getBenefitsPlanFullName = (plan: BenefitsPlan) => {
  return `${plan.descriptiveName} ${plan.name}`
}

export const getBenefitsPlanNameWithPriceEstimate = (plan: BenefitsPlan) => {
  const { provider, name, priceEstimate } = plan
  return `${titleize(provider.name)} : ${titleize(name)} ${
    priceEstimate ? ` (${extractEstimate(priceEstimate)} approx.)` : ""
  }`
}

export const isPension = (category?: string) => category === "pension"

export const isPensionEnrollment = (
  enrollment: BenefitsEnrollment | BenefitsProvisionedEnrollment,
) => {
  return isPension(enrollment.plan?.category)
}

export const getBenefitsPensionEnrollments = (engagement: Engagement) => {
  return engagement?.benefitsEnrollments?.collection?.filter(isPensionEnrollment)
}

export const getBenefitsPensionProvisionedEnrollments = (engagement: Engagement) => {
  return engagement?.benefitsProvisionedEnrollments?.collection?.filter(isPensionEnrollment)
}

export function formatAgeRange(planPrice: BenefitsPlanPrice): string {
  if (planPrice.endAge) {
    return `${planPrice.startAge} - ${planPrice.endAge}`
  } else if (!planPrice.endAge && planPrice.startAge && planPrice.startAge > 0) {
    return `${planPrice.startAge}+`
  } else {
    return "All ages"
  }
}

const getCostContribution = (costContribution: BenefitsEnrollmentCostContribution) => {
  const { id, contributor } = costContribution
  const percent = String(costContribution?.amountAsPercentage || "")
  const amount = costContribution?.amount || ""
  return { id, percent, amount, contributor }
}
/**
 * TODO: Request the appropriate costContributions from the back-end rather than assuming it's the first one.
 * @param costContributions
 * @returns
 */
export const extractContributionBreakdownValues = (
  costContributions?: BenefitsEnrollmentCostContribution[],
) => {
  const company = costContributions?.find(
    ({ contributor }) => contributor === BenefitsEnrollmentCostContributorEnum.Company,
  )

  const companyContribution = company ? getCostContribution(company) : undefined

  const tm = costContributions?.find(
    ({ contributor }) => contributor === BenefitsEnrollmentCostContributorEnum.Engagement,
  )
  const tmContribution = tm ? getCostContribution(tm) : undefined

  return {
    customerContributionId: companyContribution?.id,
    customerContributionPercent: companyContribution?.percent,
    customerContributionAmount: companyContribution?.amount,
    tmContributionId: tmContribution?.id,
    tmContributionPercent: tmContribution?.percent,
    tmContributionAmount: tmContribution?.amount,
  } as ContributorInitialValues
}

/**
 * TODO; Request the appropriate price from the back-end rather than assuming it's the last one.
 * The following situations need to be taken into account:
 * 1.The enrollment is enrolled and has an active price
 * 2.The enrollment is in applied/in progress state and doesn't have an active price yet i.e the price has an effective_date in the future
 * 3.The enrollment is terminated i.e the coverage_end_date of the last price is in the past
 */
export const getDependentEnrollmentCost = ({
  dependentEnrollmentId,
  enrollment,
}: {
  dependentEnrollmentId: string
  enrollment: BenefitsEnrollment
}) => {
  return SimpleMoney.fromNullable(
    enrollment.dependentEnrollments?.collection.find(({ id }) => id === dependentEnrollmentId)
      ?.pricePaidProvider,
  )
}

export const extractDependentEnrollmentContributionAmounts = ({
  enrollment,
  dependentEnrollmentCost,
}: {
  enrollment: BenefitsEnrollment
  dependentEnrollmentCost: SimpleMoney
}) => {
  const { customerContributionPercent, tmContributionPercent } = extractContributionBreakdownValues(
    enrollment.enrollmentCostContributions?.collection,
  )

  return {
    teamMember: {
      amount:
        dependentEnrollmentCost != null && enrollment.totalPrice && tmContributionPercent !== ""
          ? (dependentEnrollmentCost.amount / 100) * Number(tmContributionPercent)
          : undefined,
      percentage: tmContributionPercent,
    },
    company: {
      amount:
        dependentEnrollmentCost != null &&
        enrollment.totalPrice &&
        customerContributionPercent !== ""
          ? (dependentEnrollmentCost.amount / 100) * Number(customerContributionPercent)
          : undefined,
      percentage: customerContributionPercent,
    },
  }
}
